import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Box, Drawer, type SxProps, type Theme } from "@mui/material";
import { type FC, type ReactNode, useId } from "react";
import { FONT_SIZE, INK, LETTER_SPACING, NEUTRAL, SIDE_DRAWER } from "../../../theme/tokens";
import { EyebrowLabel } from "../molecules/EyebrowLabel";
import SystemButton from "../SystemButton/SystemButton";

type SharedHeaderProps = {
	hideHeader?: false;
	/** Accessible name of the close button (already translated) — "Close shift history". */
	closeLabel: string;
	/** Names the panel for the document: the drawer is a `dialog` labelled by its title. */
	title: ReactNode;
	/** The kind of panel above the title ("Shift history"), as the house eyebrow. */
	eyebrow?: ReactNode;
	/** One muted line under the title: which shift, which person, which day. */
	subtitle?: ReactNode;
	/** Drawn before the title — an identity tile, an avatar. */
	leading?: ReactNode;
	/** Under the title row, still pinned: meta chips, a filter, a toolbar. */
	headerContent?: ReactNode;
};

/** A custom reader draws its own sticky heading and close action inside the scrolling body. */
type CustomHeaderProps = {
	hideHeader: true;
	/** Accessible name of the panel, since there is no title to label it. */
	dialogLabel: string;
};

type Props = (SharedHeaderProps | CustomHeaderProps) & {
	open: boolean;
	onClose: () => void;
	/** Pinned under the scrolling body — a note about the data, a link out. */
	footer?: ReactNode;
	children: ReactNode;
	/** Width (px) on a tablet or desktop; a phone always gets the full width. */
	widthPx?: number;
	dataTestId?: string;
	bodySx?: SxProps<Theme>;
};

/**
 * A panel that slides in from the right over the page: a pinned header (eyebrow, title, subtitle, a leading tile, a
 * close button), a scrolling body and an optional pinned footer. For a detail or history the user reads without
 * leaving the screen behind it — the page stays in place underneath, so closing puts them back exactly where they
 * were. For a form that must be finished or cancelled use `SystemDialog`; for a panel that docks beside a view and
 * stays open use `CollapsibleDockPanel`.
 */
export const SystemSideDrawer: FC<Props> = (props) => {
	const {
		open,
		onClose,
		footer,
		children,
		// DEFAULT: the reading width — the size every current drawer wants; pass one for a wider inspector.
		widthPx = SIDE_DRAWER.READING_WIDTH_PX,
		dataTestId,
		bodySx,
	} = props;
	const titleId = useId();
	const header = props.hideHeader === true ? null : props;
	return (
		<Drawer
			anchor="right"
			open={open}
			onClose={onClose}
			data-testid={dataTestId}
			slotProps={{
				paper: {
					role: "dialog",
					"aria-labelledby": header === null ? undefined : titleId,
					"aria-label": props.hideHeader === true ? props.dialogLabel : undefined,
					sx: { width: { xs: "100%", sm: widthPx }, maxWidth: "100%" },
				},
			}}
		>
			<Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
				{header !== null && (
					<Box component="header" sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: `1px solid ${NEUTRAL[200]}` }}>
						<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
							{header.leading}
							<Box sx={{ flex: 1, minWidth: 0 }}>
								{header.eyebrow !== undefined && <EyebrowLabel sx={{ mb: 0.5 }}>{header.eyebrow}</EyebrowLabel>}
								<Box
									component="h2"
									id={titleId}
									sx={{
										m: 0,
										fontSize: FONT_SIZE.TITLE_MD,
										fontWeight: 700,
										lineHeight: 1.25,
										letterSpacing: LETTER_SPACING.HEADLINE,
										color: INK.PRIMARY,
										overflowWrap: "anywhere",
									}}
								>
									{header.title}
								</Box>
								{header.subtitle !== undefined && (
									<Box component="p" sx={{ m: 0, mt: 0.5, fontSize: FONT_SIZE.SECONDARY, lineHeight: 1.4, color: INK.MUTED }}>
										{header.subtitle}
									</Box>
								)}
							</Box>
							<SystemButton
								buttonVariant="borderless"
								size="compact"
								iconButton
								noFullWidthMobile
								touchTarget
								onClick={onClose}
								aria-label={header.closeLabel}
								data-testid={dataTestId ? `${dataTestId}-close` : undefined}
								sx={{ mt: -0.5, mr: -1 }}
							>
								<CloseRoundedIcon />
							</SystemButton>
						</Box>
						{header.headerContent !== undefined && <Box sx={{ mt: 1.5 }}>{header.headerContent}</Box>}
					</Box>
				)}
				<Box sx={[{ flex: 1, minHeight: 0, overflowY: "auto", px: 3, py: 2.5 }, ...(Array.isArray(bodySx) ? bodySx : [bodySx])]}>{children}</Box>
				{footer !== undefined && <Box sx={{ px: 3, py: 1.5, borderTop: `1px solid ${NEUTRAL[200]}` }}>{footer}</Box>}
			</Box>
		</Drawer>
	);
};
